import React from "react";
import "./PrintBooklet.scss";
import { getData } from "../../requests/CRUD";
import { TRAINING, TRAINING_CONTENT, PRICING_ANALYSIS } from "../../consts/tables";
import { sortArray } from "../../utils";
import { normalizeContentHtml } from "../EditorWrapper/normalizeContentHtml";

export default class PrintBooklet extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            chapters: [],
            bookletData: { cover: null, dividers: {} },
            contentMap: {},
            error: null
        };
    }

    componentDidMount() {
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.add('is-print-mode');
        }
        this.injectLandscapePageStyle();
        this.fetchAllData();
    }

    componentWillUnmount() {
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.remove('is-print-mode');
        }
        this.removeLandscapePageStyle();
    }

    // Landscape US-letter for the booklet, injected ONLY while this view is mounted.
    // This must not live in PrintBooklet.scss: that stylesheet is bundled globally,
    // so a default-page rule there forces every print in the app (LOI, invoices,
    // list prints, plain Ctrl+P) into landscape. Same pattern as PrintContinuum.js.
    //
    // Chromium quirk (verified via headless print-to-PDF): when two @page rules both
    // declare `size` with !important, the FIRST in document order wins — the reverse
    // of the normal cascade. Spec-correct engines resolve last-wins. So the SAME rule
    // goes in twice: prepended as the first node of <head> and appended last.
    injectLandscapePageStyle() {
        if (typeof document === 'undefined') return;
        const css = '@page { size: 11in 8.5in !important; margin: 0.75in !important; }';
        ['booklet-print-page-style-first', 'booklet-print-page-style-last'].forEach((id, i) => {
            if (document.getElementById(id)) return;
            const style = document.createElement('style');
            style.id = id;
            style.textContent = css;
            if (i === 0) document.head.insertBefore(style, document.head.firstChild);
            else document.head.appendChild(style);
        });
    }

    removeLandscapePageStyle() {
        if (typeof document === 'undefined') return;
        ['booklet-print-page-style-first', 'booklet-print-page-style-last'].forEach(id => {
            const el = document.getElementById(id);
            if (el && el.parentNode) el.parentNode.removeChild(el);
        });
    }

    fetchAllData() {
        const topSlug = this.props.params.slug || 'brokerage-continuum';
        
        // 1. Fetch Booklet Config
        const fetchAssets = getData({
            tableCode: PRICING_ANALYSIS,
            options: {
                filter: [{ text: 'training-booklet', code: 'PRODUCT_TYPE', type: 'exact' }],
            },
        });

        // 2. Fetch all chapters
        const fetchChapters = getData({
            tableCode: TRAINING,
            options: {
                filter: [
                    { text: '', code: 'DELETED', type: 'null' },
                    { text: topSlug, code: 'PARENT_SLUG', type: 'exact' },
                ],
                filterType: 'and',
            },
        });

        Promise.all([fetchAssets, fetchChapters]).then(([assetRes, chapterRes]) => {
            let bookletData = { cover: null, dividers: {} };
            if (assetRes && assetRes.success && assetRes.data && assetRes.data.length) {
                try {
                    bookletData = JSON.parse(assetRes.data[0].ITEMS);
                } catch (e) {}
            }

            let chapters = [];
            if (chapterRes && chapterRes.success && Array.isArray(chapterRes.data)) {
                chapters = sortArray(chapterRes.data.map(c => ({
                    id: c.SLUG,
                    name: c.NAME,
                    index: parseInt(c.INDEX),
                    subchapters: []
                })), 'index');
            }

            // 3. Fetch all subchapters and content
            const allSubchapterPromises = chapters.map(ch => getData({
                tableCode: TRAINING,
                options: {
                    filter: [
                        { text: '', code: 'DELETED', type: 'null' },
                        { text: `${topSlug}/${ch.id}`, code: 'PARENT_SLUG', type: 'exact' },
                    ],
                    filterType: 'and',
                },
            }));

            Promise.all(allSubchapterPromises).then(subResList => {
                const allContentSlugs = [];
                // Include Chapter slugs for potential content
                chapters.forEach(ch => allContentSlugs.push(`${topSlug}/${ch.id}`));
                
                subResList.forEach((res, i) => {
                    if (res && res.success && Array.isArray(res.data)) {
                        chapters[i].subchapters = sortArray(res.data.map(s => {
                            const fullSlug = `${topSlug}/${chapters[i].id}/${s.SLUG}`;
                            allContentSlugs.push(fullSlug);
                            return { id: s.SLUG, name: s.NAME, index: parseInt(s.INDEX), fullSlug };
                        }), 'index');
                    }
                });

                // 4. Fetch content for all chapters AND subchapters
                const contentPromises = allContentSlugs.map(slug => getData({
                    tableCode: TRAINING_CONTENT,
                    options: {
                        filter: [
                            { text: '', code: 'DELETED', type: 'null' },
                            { text: slug, code: 'CHAPTER_SLUG', type: 'exact' },
                        ],
                        filterType: 'and',
                    },
                }));

                Promise.all(contentPromises).then(contentResList => {
                    const contentMap = {};
                    contentResList.forEach((res, i) => {
                        if (res && res.success && res.data && res.data.length) {
                            contentMap[allContentSlugs[i]] = res.data[0].CONTENT;
                        }
                    });

                    this.setState({ chapters, bookletData, contentMap, loading: false }, () => {
                        // Delay slightly to ensure fonts/images/PDF objects have a chance to start loading
                        setTimeout(() => window.print(), 1000);
                    });
                });
            });
        }).catch(err => {
            this.setState({ error: "Failed to load booklet data", loading: false });
        });
    }

    renderPage(content, type = 'content', title = '') {
        if (!content) return null;
        // Editors (TinyMCE) frequently append trailing empty/whitespace paragraphs and
        // <br>s. Left in, that empty tail can spill onto a blank page in print. Strip any
        // run of trailing whitespace / <br> / empty <p>|<div> from the end of the content.
        const cleaned = String(content).replace(
            /(?:\s|&nbsp;|<br\s*\/?>|<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>|<div>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>)+$/gi,
            ''
        );
        if (!cleaned.trim()) return null;
        return (
            <div className={`booklet-page booklet-page--${type}`}>
                {title && <h1 className="booklet-page-title">{title}</h1>}
                <div className="booklet-page-content" dangerouslySetInnerHTML={{ __html: normalizeContentHtml(cleaned) }} />
            </div>
        );
    }

    renderDivider(url, title) {
        if (!url) return null;
        const isPdf = url.toLowerCase().endsWith('.pdf');
        
        return (
            <div className="booklet-page booklet-page--divider">
                {isPdf ? (
                    <object data={url} type="application/pdf" width="100%" height="100%">
                        <p>PDF divider: <a href={url}>{title}</a></p>
                    </object>
                ) : (
                    <img src={url} alt={title} />
                )}
            </div>
        );
    }

    render() {
        const { loading, chapters, bookletData, contentMap, error } = this.state;
        const topSlug = this.props.params.slug || 'brokerage-continuum';
        const chapterSlug = this.props.params.chapterSlug;

        if (loading) return <div className="PrintBooklet_loading">Preparing booklet for print...</div>;
        if (error) return <div className="PrintBooklet_error">{error}</div>;

        // The "Print Section" button emits {topSlug}/{topSlug} when no specific
        // sub-chapter is open (it falls back from slug2 to slug). Treat chapterSlug ===
        // topSlug as "print the whole section" and show all chapters, otherwise the
        // filter matches no chapter (none is named after the section) and the page is blank.
        const filteredChapters = (chapterSlug && chapterSlug !== topSlug)
            ? chapters.filter(ch => ch.id === chapterSlug)
            : chapters;

        return (
            <div className="PrintBooklet">
                {/* 1. Cover (only for full booklet or if we want it for sections too? Usually full only) */}
                {!chapterSlug && this.renderDivider(bookletData.cover, "Cover")}

                {/* 2. Chapters */}
                {filteredChapters.map(ch => (
                    <React.Fragment key={ch.id}>
                        {/* Chapter Divider */}
                        {this.renderDivider(bookletData.dividers[ch.id], ch.name)}

                        {/* Chapter Introduction/Content (if any) */}
                        {this.renderPage(contentMap[`${topSlug}/${ch.id}`], 'content', ch.name)}

                        {/* Subchapters */}
                        {ch.subchapters.map(sub => (
                            <React.Fragment key={sub.id}>
                                {/* Subchapter Divider */}
                                {this.renderDivider(bookletData.dividers[`${ch.id}/${sub.id}`], sub.name)}
                                
                                {/* Subchapter Content */}
                                {this.renderPage(contentMap[sub.fullSlug], 'content', sub.name)}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}

                {/* Back cover (full booklet only) */}
                {!chapterSlug && this.renderDivider(bookletData.backCover, "Back Cover")}
            </div>
        );
    }
}
